// TanStack Query keys + fetchers for the /admin/crypto-wallet/*
// surface (SM-211 crypto rail). Mirrors the shape of queries.ts but
// lives in its own file so the Wallet Ops surface is self-contained
// (same split ops-queries.ts uses for /admin/ops/*).
//
// Paginated fetchers normalize the wire envelope
// `{ <items_field>: [...], page: PageInfo }` into Paginated<T> like
// the queries.ts helpers do.

import { api, APIError, qs } from "./api";
import type { PageInfo, Paginated } from "./types";
import type {
  SystemAccountCryptoDeposit,
  SystemAccountCryptoWithdrawal,
  SystemAccountDepositAddress,
  CryptoAsset,
  CryptoBlacklistEntry,
  CryptoCollectionConfig,
  CryptoCollectionOrder,
  CryptoDepositOrder,
  CryptoFlags,
  CryptoGasConfig,
  CryptoGasOrder,
  CryptoHotLiquidityStatus,
  CryptoLiquidityConfig,
  CryptoNetwork,
  CryptoPreviewCredit,
  CryptoRate,
  CryptoRefill,
  CryptoWithdrawalConfig,
  CryptoWithdrawalOrder,
  CustodyBalanceSnapshot,
  CustodyWallet,
  PipelineStatus,
  RailDirection,
  RailLevel,
  RailPutInput,
  RailResolved,
  RailSnapshot,
  RpcTestResult,
} from "./wallet-types";

export const walletKeys = {
  all: ["crypto-wallet"] as const,
  // networks() is the invalidation PREFIX; networksList(flag) is the actual
  // query key. Prefix-matching means one invalidate refreshes both the
  // with- and without-archived views.
  networks: () => [...walletKeys.all, "networks"] as const,
  networksList: (includeArchived: boolean) =>
    [...walletKeys.all, "networks", includeArchived] as const,
  assets: (networkID?: string) =>
    [...walletKeys.all, "assets", networkID ?? ""] as const,
  rates: (assetID: string) => [...walletKeys.all, "rates", assetID] as const,
  flags: () => [...walletKeys.all, "flags"] as const,
  withdrawalConfig: (assetID: string) =>
    [...walletKeys.all, "withdrawal-config", assetID] as const,
  withdrawalConfigs: () =>
    [...walletKeys.all, "withdrawal-configs"] as const,
  gasConfig: (networkID: string) =>
    [...walletKeys.all, "gas-config", networkID] as const,
  gasConfigs: () => [...walletKeys.all, "gas-configs"] as const,
  collectionConfig: (assetID: string) =>
    [...walletKeys.all, "collection-config", assetID] as const,
  collectionConfigs: () =>
    [...walletKeys.all, "collection-configs"] as const,
  liquidityConfigs: () =>
    [...walletKeys.all, "liquidity-configs"] as const,
  hotLiquidity: () => [...walletKeys.all, "hot-liquidity"] as const,
  refillsAll: () => [...walletKeys.all, "refills"] as const,
  refills: (status: string | undefined, limit: number, offset: number) =>
    [...walletKeys.all, "refills", status ?? "", limit, offset] as const,
  blacklist: (networkID: string | undefined, limit: number, offset: number) =>
    [...walletKeys.all, "blacklist", networkID ?? "", limit, offset] as const,
  orders: (
    tab: string,
    status: string | undefined,
    limit: number,
    offset: number,
  ) => [...walletKeys.all, "orders", tab, status ?? "", limit, offset] as const,
  // custodyAll()/custodyBalancesAll() are invalidation PREFIXES (same
  // split networks() uses) — provisioning a system wallet invalidates
  // both so the directory and its balance snapshots refresh together.
  custodyAll: () => [...walletKeys.all, "custody"] as const,
  custody: (
    networkID: string | undefined,
    walletType: string | undefined,
    limit: number,
    offset: number,
  ) =>
    [
      ...walletKeys.custodyAll(),
      networkID ?? "",
      walletType ?? "",
      limit,
      offset,
    ] as const,
  custodyBalancesAll: () => [...walletKeys.all, "custody-balances"] as const,
  custodyBalances: (networkID: string, addresses: string[]) =>
    [...walletKeys.custodyBalancesAll(), networkID, addresses] as const,
  pipeline: (networkID?: string) =>
    [...walletKeys.all, "pipeline", networkID ?? ""] as const,
};

// --- networks / assets ---

export const fetchCryptoNetworks = (includeArchived = false) =>
  api<{ networks: CryptoNetwork[] }>(
    `/admin/crypto-wallet/networks${qs({
      include_archived: includeArchived ? "true" : undefined,
    })}`,
  ).then((r) => r.networks ?? []);

// Retire a network, or restore it. Reason is required server-side — it
// lands in the audit summary, which is what answers "why was this chain
// retired" months later.
export const setCryptoNetworkArchived = (
  network_id: string,
  archived: boolean,
  reason: string,
) =>
  api(`/admin/crypto-wallet/networks/archive${qs({ reason })}`, {
    method: "POST",
    body: { network_id, archived },
  });

export const upsertCryptoNetwork = (input: CryptoNetwork) =>
  api<CryptoNetwork>("/admin/crypto-wallet/networks", {
    method: "PUT",
    body: input,
  });

export const testCryptoNetworkRpc = (rpc_url: string, chain_id: number) =>
  api<RpcTestResult>("/admin/crypto-wallet/networks/test-rpc", {
    method: "POST",
    body: { rpc_url, chain_id },
  });

export const fetchCryptoAssets = (networkID?: string) =>
  api<{ assets: CryptoAsset[] }>(
    `/admin/crypto-wallet/assets${qs({ network_id: networkID })}`,
  ).then((r) => r.assets ?? []);

export const upsertCryptoAsset = (input: CryptoAsset) =>
  api<CryptoAsset>("/admin/crypto-wallet/assets", {
    method: "PUT",
    body: input,
  });

// --- rates + preview ---

export const fetchCryptoRates = (assetID: string) =>
  api<{ rates: CryptoRate[] }>(
    `/admin/crypto-wallet/assets/${assetID}/rates`,
  ).then((r) => r.rates ?? []);

/**
 * `unit_version` for a rate this console writes (SM-513): the figure on
 * the wire is a whole-currency-unit rate scaled by the currency's minor
 * scale. Rows without it are legacy — written by the editor that scaled
 * by 100 — and the service keeps them apart on exactly this field.
 */
export const RATE_UNIT_MAJOR = 2;

// SM-244: operators configure a single "per token unit" figure. The
// legacy numerator/denominator pair is pinned to 1:1 on the wire (the API
// still carries the fields).
//
// SM-416/SM-504: a rate belongs to an (asset, CURRENCY) mapping, not to
// the asset. The service supersedes the previous active row FOR THAT
// CURRENCY only, so republishing an asset's RED rate leaves its GOLD
// one exactly where it was. The currency is always sent rather than
// left to the server's GOLD default — a rates table that edits two
// mappings per asset must never rely on which one is the default.
//
// SM-513: `sparkMinorPerUnit` is MINOR units per whole token, and the
// caller is the one that scaled the operator's whole-unit entry — see
// rateMajorToMinor. unit_version: 2 states that, and it is the whole
// point of sending it: the service records provenance rather than
// assuming it, so the rows written by an editor that scaled by 100 stay
// distinguishable from the ones written by this call.
export const setCryptoRate = (
  assetID: string,
  sparkMinorPerUnit: number,
  currency: string,
) =>
  api<CryptoRate>(`/admin/crypto-wallet/assets/${assetID}/rates`, {
    method: "POST",
    body: {
      rate_num: 1,
      rate_den: 1,
      spark_minor_per_unit: sparkMinorPerUnit,
      unit_version: RATE_UNIT_MAJOR,
      credited_currency: currency,
    },
  });

/**
 * Retire one mapping's active rate without replacing it (SM-507). The
 * mapping keeps its configuration and its history and simply stops
 * being able to credit — which is a different act from repricing, and
 * is audited as one.
 */
export const retireCryptoRate = (assetID: string, currency: string) =>
  api<{ asset_id: string; currency: string; retired: boolean }>(
    `/admin/crypto-wallet/assets/${assetID}/rates/${currency}`,
    { method: "DELETE" },
  );

export const previewCryptoCredit = (
  assetID: string,
  amountBase: string,
  currency: string,
) =>
  api<CryptoPreviewCredit>(
    `/admin/crypto-wallet/assets/${assetID}/preview-credit`,
    { method: "POST", body: { amount_base: amountBase, currency } },
  );

// --- feature flags ---

export const fetchCryptoFlags = () =>
  api<CryptoFlags>("/admin/crypto-wallet/flags");

export const setCryptoFlags = (input: CryptoFlags) =>
  api<CryptoFlags>("/admin/crypto-wallet/flags", {
    method: "PUT",
    body: input,
  });

// --- policies ---
// Each type has a list (backs the SM-278 policy pages), a create-only
// POST (409 when the asset/network already has a policy), and a PUT
// that edits the existing row.

export const fetchWithdrawalConfig = (assetID: string) =>
  api<CryptoWithdrawalConfig>(
    `/admin/crypto-wallet/withdrawal-configs/${assetID}`,
  );

export const fetchWithdrawalConfigs = () =>
  api<{ configs: CryptoWithdrawalConfig[] }>(
    "/admin/crypto-wallet/withdrawal-configs",
  ).then((r) => r.configs);

export const createWithdrawalConfig = (input: CryptoWithdrawalConfig) =>
  api<CryptoWithdrawalConfig>("/admin/crypto-wallet/withdrawal-configs", {
    method: "POST",
    body: input,
  });

export const putWithdrawalConfig = (input: CryptoWithdrawalConfig) =>
  api<CryptoWithdrawalConfig>("/admin/crypto-wallet/withdrawal-configs", {
    method: "PUT",
    body: input,
  });

export const fetchGasConfig = (networkID: string) =>
  api<CryptoGasConfig>(`/admin/crypto-wallet/gas-configs/${networkID}`);

export const fetchGasConfigs = () =>
  api<{ configs: CryptoGasConfig[] }>("/admin/crypto-wallet/gas-configs").then(
    (r) => r.configs,
  );

export const createGasConfig = (input: CryptoGasConfig) =>
  api<CryptoGasConfig>("/admin/crypto-wallet/gas-configs", {
    method: "POST",
    body: input,
  });

export const putGasConfig = (input: CryptoGasConfig) =>
  api<CryptoGasConfig>("/admin/crypto-wallet/gas-configs", {
    method: "PUT",
    body: input,
  });

export const fetchCollectionConfig = (assetID: string) =>
  api<CryptoCollectionConfig>(
    `/admin/crypto-wallet/collection-configs/${assetID}`,
  );

export const fetchCollectionConfigs = () =>
  api<{ configs: CryptoCollectionConfig[] }>(
    "/admin/crypto-wallet/collection-configs",
  ).then((r) => r.configs);

export const createCollectionConfig = (input: CryptoCollectionConfig) =>
  api<CryptoCollectionConfig>("/admin/crypto-wallet/collection-configs", {
    method: "POST",
    body: input,
  });

export const putCollectionConfig = (input: CryptoCollectionConfig) =>
  api<CryptoCollectionConfig>("/admin/crypto-wallet/collection-configs", {
    method: "PUT",
    body: input,
  });

// --- SM-279: hot-wallet liquidity + COLD→HOT refills ---

export const fetchLiquidityConfigs = () =>
  api<{ configs: CryptoLiquidityConfig[] }>(
    "/admin/crypto-wallet/hot-liquidity-configs",
  ).then((r) => r.configs ?? []);

export const putLiquidityConfig = (input: CryptoLiquidityConfig) =>
  api<CryptoLiquidityConfig>("/admin/crypto-wallet/hot-liquidity-configs", {
    method: "PUT",
    body: input,
  });

export const fetchHotLiquidity = () =>
  api<{ statuses: CryptoHotLiquidityStatus[] }>(
    "/admin/crypto-wallet/hot-liquidity",
  ).then((r) => r.statuses ?? []);

export const fetchRefills = (
  status: string | undefined,
  limit: number,
  offset: number,
) =>
  api<{ refills: CryptoRefill[]; page: PageInfo }>(
    `/admin/crypto-wallet/refills${qs({ status, limit, offset })}`,
  );

export const createRefill = (input: { asset_id: string; amount_base: string }) =>
  api<CryptoRefill>("/admin/crypto-wallet/refills", {
    method: "POST",
    body: input,
  });

// Step-up bridge for approvals: start issues an MFA challenge (or says
// none is needed), verify swaps a TOTP code for a short-lived token the
// approve call requires.
export const refillStepUpStart = () =>
  api<{
    mfa_required: boolean;
    challenge_id?: string;
    factors?: { factor_id: string; kind: string; friendly_name: string }[];
  }>("/admin/crypto-wallet/refills/stepup/start", { method: "POST", body: {} });

export const refillStepUpVerify = (input: {
  challenge_id: string;
  factor_id: string;
  code: string;
}) =>
  api<{ stepup_token: string; expires_in: number }>(
    "/admin/crypto-wallet/refills/stepup/verify",
    { method: "POST", body: input },
  );

export const approveRefill = (refillID: string, stepUpToken: string) =>
  api<CryptoRefill>(
    `/admin/crypto-wallet/refills/${encodeURIComponent(refillID)}/approve`,
    { method: "POST", body: { step_up_token: stepUpToken } },
  );

export const rejectRefill = (refillID: string, reason: string) =>
  api<CryptoRefill>(
    `/admin/crypto-wallet/refills/${encodeURIComponent(refillID)}/reject`,
    { method: "POST", body: { reason } },
  );

// --- blacklist ---

export const fetchCryptoBlacklist = (opts: {
  network_id?: string;
  limit: number;
  offset: number;
}) =>
  api<{ entries: CryptoBlacklistEntry[]; page: Paginated<never>["page"] }>(
    `/admin/crypto-wallet/blacklist${qs({
      network_id: opts.network_id,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  ).then<Paginated<CryptoBlacklistEntry>>((r) => ({
    items: r.entries ?? [],
    page: r.page,
  }));

export const addCryptoBlacklist = (input: {
  network_id: string;
  address: string;
  reason: string;
}) =>
  api("/admin/crypto-wallet/blacklist", { method: "POST", body: input });

// reason travels as a query param — the backoffice logs it in the
// audit summary (the upstream DELETE carries no body).
export const removeCryptoBlacklist = (
  networkID: string,
  address: string,
  reason: string,
) =>
  api(
    `/admin/crypto-wallet/blacklist/${networkID}/${address}${qs({ reason })}`,
    { method: "DELETE" },
  );

// --- order lists ---

interface OrderListOpts {
  status?: string;
  limit: number;
  offset: number;
}

const orderQS = (o: OrderListOpts) =>
  qs({ status: o.status, limit: o.limit, offset: o.offset });

export const fetchCryptoDeposits = (o: OrderListOpts) =>
  api<{ deposits: CryptoDepositOrder[]; page: Paginated<never>["page"] }>(
    `/admin/crypto-wallet/orders/deposits${orderQS(o)}`,
  ).then<Paginated<CryptoDepositOrder>>((r) => ({
    items: r.deposits ?? [],
    page: r.page,
  }));

export const fetchCryptoWithdrawals = (o: OrderListOpts) =>
  api<{ withdrawals: CryptoWithdrawalOrder[]; page: Paginated<never>["page"] }>(
    `/admin/crypto-wallet/orders/withdrawals${orderQS(o)}`,
  ).then<Paginated<CryptoWithdrawalOrder>>((r) => ({
    items: r.withdrawals ?? [],
    page: r.page,
  }));

export const fetchCryptoGasOrders = (o: OrderListOpts) =>
  api<{ orders: CryptoGasOrder[]; page: Paginated<never>["page"] }>(
    `/admin/crypto-wallet/orders/gas${orderQS(o)}`,
  ).then<Paginated<CryptoGasOrder>>((r) => ({
    items: r.orders ?? [],
    page: r.page,
  }));

export const fetchCryptoCollectionOrders = (o: OrderListOpts) =>
  api<{ orders: CryptoCollectionOrder[]; page: Paginated<never>["page"] }>(
    `/admin/crypto-wallet/orders/collections${orderQS(o)}`,
  ).then<Paginated<CryptoCollectionOrder>>((r) => ({
    items: r.orders ?? [],
    page: r.page,
  }));

// --- custody ---

export const fetchCustodyWallets = (opts: {
  network_id?: string;
  wallet_type?: string;
  limit: number;
  offset: number;
}) =>
  api<{ wallets: CustodyWallet[]; page: Paginated<never>["page"] }>(
    `/admin/crypto-wallet/custody${qs({
      network_id: opts.network_id,
      wallet_type: opts.wallet_type,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  ).then<Paginated<CustodyWallet>>((r) => ({
    items: r.wallets ?? [],
    page: r.page,
  }));

// Balances are per network because the RPC endpoint is: callers group
// the wallets they are showing by network_id and fetch one snapshot per
// group. The upstream caps the address list at 100.
// Get-or-create a system wallet (SM-262 stage 2 — onboarding wizard).
// Idempotent server-side: re-running returns the existing wallet.
export const provisionCryptoSystemWallet = (
  network_id: string,
  wallet_type: "HOT" | "COLD" | "GAS",
) =>
  api<CustodyWallet>("/admin/crypto-wallet/custody/system", {
    method: "POST",
    body: { network_id, wallet_type },
  });

export const fetchCustodyBalances = (networkID: string, addresses: string[]) =>
  api<CustodyBalanceSnapshot>(
    `/admin/crypto-wallet/custody/balances${qs({
      network_id: networkID,
      addresses: addresses.join(","),
    })}`,
  );

// --- pipeline health ---

export const fetchPipelineStatus = (networkID?: string) =>
  api<PipelineStatus>(
    `/admin/crypto-wallet/pipeline/status${qs({ network_id: networkID })}`,
  );

// --- pipeline operations (SM-254 safe replay / reconcile) ---
// Every op is gated on CRYPTO_WALLET_MANUAL_OP and demands an audit
// reason (backoffice-service requires the ?reason= query param and 400s
// with BO_REASON_REQUIRED otherwise). The JSON body carries network_id
// and, for cursor-override, the target block. cursor-override is refused
// (409 CWCHAIN_000204) unless the scanner is already PAUSED.
export type PipelineOp =
  | "pause"
  | "resume"
  | "cursor-override"
  | "retry-failed";

export const cryptoPipelineOp = (
  op: PipelineOp,
  body: Record<string, unknown>,
  reason: string,
) =>
  api(`/admin/crypto-wallet/pipeline/${op}${qs({ reason })}`, {
    method: "POST",
    body,
  });

// --- system-account crypto rail (SM-335) ---
//
// These ride /admin/system-accounts/{code}/crypto/* rather than
// /admin/crypto-wallet/*, but they live in this module because
// everything they return is crypto-rail shaped (base-unit amounts,
// networks, assets, tx hashes) and the card that renders them already
// depends on this file's networks / assets / rates fetchers. The ledger
// half of the same account stays in queries.ts under keys.treasury; a
// move on this surface changes BOTH, so callers invalidate
// walletKeys.systemAccountAll(code) and the treasury keys together.
//
// Backoffice enforces an intersection of SYSTEM_ACCOUNT_* and
// CRYPTO_WALLET_VIEW on all five, so a 403 here means the operator is
// missing one of the two halves.

export const systemAccountWalletKeys = {
  /** Invalidation prefix for everything on-chain about one account. */
  all: (code: string) =>
    [...walletKeys.all, "system-account", code] as const,
  depositAddress: (code: string, networkID: string) =>
    [...walletKeys.all, "system-account", code, "deposit-address", networkID] as const,
  deposits: (code: string, limit: number, offset: number) =>
    [...walletKeys.all, "system-account", code, "deposits", limit, offset] as const,
  withdrawals: (code: string, limit: number, offset: number) =>
    [...walletKeys.all, "system-account", code, "withdrawals", limit, offset] as const,
};

const sysAcctPath = (code: string, tail: string) =>
  `/admin/system-accounts/${encodeURIComponent(code)}/crypto/${tail}`;

/**
 * Reads the account's deposit address on a network. Resolves to null on
 * a 404 — "not provisioned yet" is a normal state the card renders a
 * Provision button for, not an error worth an ErrorPanel.
 */
export const fetchSystemAccountDepositAddress = (
  code: string,
  networkID: string,
) =>
  api<SystemAccountDepositAddress>(
    `${sysAcctPath(code, "deposit-address")}${qs({ network_id: networkID })}`,
  ).catch((e: unknown) => {
    if (e instanceof APIError && e.status === 404) return null;
    throw e;
  });

/** Get-or-create. Idempotent upstream; `created` says which happened. */
export const provisionSystemAccountDepositAddress = (
  code: string,
  networkID: string,
) =>
  api<SystemAccountDepositAddress>(sysAcctPath(code, "deposit-address"), {
    method: "POST",
    body: { network_id: networkID },
  });

// The list envelopes carry `page` when the server paginates. Older
// builds may return the bare array; normalize both so the caller always
// sees a Paginated<T> and the Pagination control can trust page.total.
function pagedOr<T>(items: T[] | undefined, page: PageInfo | undefined, limit: number, offset: number): Paginated<T> {
  const list = items ?? [];
  return {
    items: list,
    page: page ?? {
      total: offset + list.length,
      limit,
      offset,
      has_more: list.length >= limit,
    },
  };
}

export const fetchSystemAccountCryptoDeposits = (
  code: string,
  opts: { limit: number; offset: number },
) =>
  api<{ deposits: SystemAccountCryptoDeposit[]; page?: PageInfo }>(
    `${sysAcctPath(code, "deposits")}${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then<Paginated<SystemAccountCryptoDeposit>>((r) =>
    pagedOr(r.deposits, r.page, opts.limit, opts.offset),
  );

export const fetchSystemAccountCryptoWithdrawals = (
  code: string,
  opts: { limit: number; offset: number },
) =>
  api<{ withdrawals: SystemAccountCryptoWithdrawal[]; page?: PageInfo }>(
    `${sysAcctPath(code, "withdrawals")}${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then<Paginated<SystemAccountCryptoWithdrawal>>((r) =>
    pagedOr(r.withdrawals, r.page, opts.limit, opts.offset),
  );

export interface SystemAccountWithdrawalInput {
  asset_id: string;
  destination: string;
  /** Token amount in BASE units (integer string at the asset's decimals). */
  amount_base: string;
  reason: string;
}

/**
 * Opens the on-chain send. `idempotencyKey` is the caller's per-confirm
 * key and travels BOTH as the Idempotency-Key header and as the
 * idempotency_key body field — crypto-wallet-service's partial unique
 * index keys on the body field, and the gateway allowlists the header.
 * The caller mints it once per confirm (useRef) so a retry after a lost
 * response resolves the original send instead of broadcasting a second.
 */
export const createSystemAccountWithdrawal = (
  code: string,
  input: SystemAccountWithdrawalInput,
  idempotencyKey: string,
) =>
  api<SystemAccountCryptoWithdrawal>(sysAcctPath(code, "withdrawals"), {
    method: "POST",
    body: { ...input, idempotency_key: idempotencyKey },
    idempotencyKey,
  });

// --- deposit / withdrawal configuration hierarchy (SM-504) ---

export const railKeys = {
  all: [...walletKeys.all, "rails"] as const,
  snapshot: () => [...railKeys.all, "snapshot"] as const,
  resolve: (direction: string, currency: string, assetID?: string) =>
    [...railKeys.all, "resolve", direction, currency, assetID ?? ""] as const,
};

/**
 * The whole hierarchy in one read. Deliberately ONE request rather than
 * one per level: the screen has to show configured and effective values
 * side by side, and three independent queries would let it render a
 * half-resolved state where a switch and its consequence disagree.
 */
export const fetchRailSnapshot = () =>
  api<RailSnapshot>("/admin/crypto-wallet/rails");

/** One route's resolved availability — the read-only summary. */
export const fetchRailResolved = (
  direction: RailDirection,
  currency: string,
  assetID?: string,
) =>
  api<RailResolved>(
    `/admin/crypto-wallet/rails/resolve${qs({
      direction,
      currency,
      asset_id: assetID,
    })}`,
  );

/**
 * What a pending change WOULD resolve to, without saving it. The
 * preview matters most for the case the screen cannot show any other
 * way: enabling a switch that is still blocked two levels up.
 */
export const previewRail = (input: {
  level: RailLevel;
  preview_currency?: string;
  preview_asset_id?: string;
} & RailPutInput) =>
  api<RailResolved>("/admin/crypto-wallet/rails/preview", {
    method: "POST",
    body: input,
  });

/** Each PUT writes exactly ONE level and touches nothing else. */
export const putRailMode = (input: RailPutInput) =>
  api<{ saved: boolean; resolved?: RailResolved }>(
    "/admin/crypto-wallet/rails/modes",
    { method: "PUT", body: input },
  );

export const putRailCurrency = (input: RailPutInput) =>
  api<{ saved: boolean; resolved?: RailResolved }>(
    "/admin/crypto-wallet/rails/currencies",
    { method: "PUT", body: input },
  );

export const putRailAssetCurrency = (input: RailPutInput) =>
  api<{ saved: boolean; resolved?: RailResolved }>(
    "/admin/crypto-wallet/rails/asset-currencies",
    { method: "PUT", body: input },
  );

/**
 * Remove a mapping. Distinct from disabling one: a disabled mapping
 * still records that the asset is configured for that currency.
 */
export const deleteRailAssetCurrency = (
  assetID: string,
  currency: string,
  direction: RailDirection,
) =>
  api(
    `/admin/crypto-wallet/rails/asset-currencies/${encodeURIComponent(assetID)}/${encodeURIComponent(currency)}/${encodeURIComponent(direction)}`,
    { method: "DELETE" },
  );
